/**
 * Contract helpers for the "store orders visibility" tenant setting (RTLOMS-637).
 *
 * Mirrors the back-end model exposed under `display.orders_visibility` (RTLOMS-635):
 * the configuration is a list of blocks combined with OR; each block is a list of
 * conditions combined with AND; each condition is `{ field, operator, values_to_check }`.
 * Field/operator identifiers are kept in the exact snake_case casing expected by the API.
 */

/**
 * Magic value flagging a condition as contextual to the connected user's store.
 * Encoded as a plain string (no wrapper object) exactly like the back-end.
 */
export const CURRENT_USER_LOCATION = 'CURRENT_USER_LOCATION'

/** Location-typed fields: their value can be CURRENT_USER_LOCATION or static location id(s). */
export const LOCATION_FIELDS = ['pickup_location_id', 'source_location_id', 'origin_location_id']

/** All selectable fields, in display order. */
export const ORDER_VISIBILITY_FIELDS = [...LOCATION_FIELDS, 'current_status']

/** Selectable operators, matching the back-end `OPERATORS` enum (RTLOMS-635). */
export const ORDER_VISIBILITY_OPERATORS = ['equals', 'not_equals', 'in', 'not_in']

/** Operators whose `values_to_check` is an array of values instead of a scalar. */
export const MULTI_VALUE_OPERATORS = ['in', 'not_in']

/** i18n key per field (reused by the block and the help modal). */
export const FIELD_LABEL_KEYS = {
  pickup_location_id: 'settings.ordersVisibility.fields.pickupLocationId',
  source_location_id: 'settings.ordersVisibility.fields.sourceLocationId',
  origin_location_id: 'settings.ordersVisibility.fields.originLocationId',
  current_status: 'settings.ordersVisibility.fields.currentStatus',
}

/**
 * Order statuses selectable for the `current_status` field.
 * The back-end does not constrain the status enum, so the front-end owns this list;
 * labels reuse the existing `order.*` / `subOrder.*` translations.
 */
export const ORDER_STATUS_OPTIONS = [
  { value: 'waiting', labelKey: 'order.waiting' },
  { value: 'waiting_payment', labelKey: 'order.waitingPayment' },
  { value: 'confirmed', labelKey: 'order.confirmed' },
  { value: 'partially_confirmed', labelKey: 'order.partiallyConfirmed' },
  { value: 'to_pickup', labelKey: 'order.toPickup' },
  { value: 'waiting_carrier', labelKey: 'order.waitingCarrier' },
  { value: 'shipping_in_progress', labelKey: 'order.shippingInProgress' },
  { value: 'partial_shipping_in_progress', labelKey: 'order.partialShippingInProgress' },
  { value: 'delivered', labelKey: 'order.delivered' },
  { value: 'partially_delivered', labelKey: 'order.deliveredPartially' },
  { value: 'cancelled', labelKey: 'order.cancelled' },
  { value: 'waiting_allocation', labelKey: 'subOrder.waitingAllocation' },
  { value: 'blocked', labelKey: 'order.blocked' },
  { value: 'error', labelKey: 'order.error' },
]

/**
 * Local fallback default, kept in sync with the back-end default configuration.
 *
 * Source of truth: oms-api-v2 `src/schemas/constants/ordersVisibilityDefault.js`
 * (ticket RTLOMS-635). Only used when the API response does not carry
 * `display.orders_visibility.default_conditions` (e.g. the brand-new country creation
 * flow); otherwise the API value is authoritative. Keep this in sync if the back-end
 * default changes.
 */
export const DEFAULT_ORDERS_VISIBILITY_CONDITIONS = [
  [
    { field: 'pickup_location_id', operator: 'equals', values_to_check: CURRENT_USER_LOCATION },
    { field: 'current_status', operator: 'in', values_to_check: ['partially_delivered', 'delivered', 'cancelled'] },
  ],
]

/**
 * @param {string} operator
 * @returns {boolean} whether the operator expects an array of values.
 */
export function isMultiValueOperator(operator) {
  return MULTI_VALUE_OPERATORS.includes(operator)
}

/**
 * @param {string} field
 * @returns {boolean} whether the field is a location-typed field.
 */
export function isLocationField(field) {
  return LOCATION_FIELDS.includes(field)
}

/**
 * Deep-clones a conditions structure so the form never shares references with a
 * default/source array (immutable update friendly).
 *
 * @param {Array} conditions
 * @returns {Array}
 */
export function cloneConditions(conditions) {
  return structuredClone(Array.isArray(conditions) ? conditions : [])
}

/**
 * Pure business validation for an orders-visibility configuration, mirrored from the
 * back-end. Returns the i18n key of the first violated rule, or null when valid.
 * Kept here (rather than in the settings view) so it stays testable in isolation.
 *
 * @param {Array} conditions
 * @returns {string|null}
 */
export function validateOrdersVisibilityConditions(conditions) {
  if (!Array.isArray(conditions) || !conditions.length) {
    return 'settings.ordersVisibility.errors.noBlock'
  }
  if (conditions.some((block) => !Array.isArray(block) || !block.length)) {
    return 'settings.ordersVisibility.errors.emptyBlock'
  }
  if (!hasCurrentUserLocationReference(conditions)) {
    return 'settings.ordersVisibility.errors.currentUserLocationRequired'
  }
  return null
}

/**
 * Validation rule mirrored from the back-end: at least one condition (in any block)
 * must reference the CURRENT_USER_LOCATION variable.
 *
 * @param {Array} conditions
 * @returns {boolean}
 */
export function hasCurrentUserLocationReference(conditions) {
  if (!Array.isArray(conditions)) {
    return false
  }
  return conditions.some(
    (block) =>
      Array.isArray(block) &&
      block.some((condition) => {
        // Only a location-typed field can meaningfully reference the variable.
        if (!isLocationField(condition?.field)) {
          return false
        }
        const values = condition.values_to_check
        return Array.isArray(values) ? values.includes(CURRENT_USER_LOCATION) : values === CURRENT_USER_LOCATION
      })
  )
}
